import {
    barcodeTypeLabels,
    ComponentTypes,
    ITemplateAutonumberComponent,
    ITemplateBarcodeComponent,
    ITemplateComponent,
    ITemplateSaveAndTicketComponent,
    ITemplateTrackerIntegrationComponent
} from "entities/template";
import React, { useMemo } from "react";
import { IComponentConfig, useComponentsConfig } from "./useComponentsConfig";

interface IComponentLabelProps {
    component?: ITemplateComponent,
    components?: ITemplateComponent[],
    multiple: boolean;
    displayEnLabel: boolean;
}

export function useComponentLabel({ component, components, multiple, displayEnLabel }: IComponentLabelProps): string | Record<string, string | undefined> {
    const componentsConfig = useComponentsConfig();
    const config = useMemo(() => {
        return component ? componentsConfig[component.type] : undefined;
    }, [component, componentsConfig])

    const getComponentLabel = (component: ITemplateComponent, config: IComponentConfig, displayEnLabel: boolean) => {
        switch (component.type) {
            case ComponentTypes.barcode:
                return `${config.label}: ${barcodeTypeLabels[(component as ITemplateBarcodeComponent).barcodeType]}`
            case ComponentTypes.trackerIntegration:
                const fieldName = (component as ITemplateTrackerIntegrationComponent).startrekFields?.name;
                return `${config.label}: ${fieldName ? fieldName : ""}`
            case ComponentTypes.saveAndTicket:
                const queue = (component as ITemplateSaveAndTicketComponent).startrekQueue;
                return `${config.label}: ${queue ? queue : ""}`
            case ComponentTypes.autonumber:
                return (component as ITemplateAutonumberComponent).counterName || config.label
            case ComponentTypes.language:
            case ComponentTypes.preview:
            case ComponentTypes.externalForm:
            case ComponentTypes.uuid:
            case ComponentTypes.massDocGeneration:
                return `${config.label}`
            default:
                return displayEnLabel && component.label_en ? component.label_en : component.label;
        }
    }

    const label = useMemo(() => {
        if (component && config) {
            return getComponentLabel(component, config, displayEnLabel)
        }
    }, [component, displayEnLabel, config]);

    const multipleLabels = useMemo(() => {
        const componentsObj: Record<string, string | undefined> = {};
        if (components && components.length > 0 && componentsConfig) {
            components.forEach((c) => {
                const config = componentsConfig[c.type];
                const componentLabel = getComponentLabel(c, config, displayEnLabel);
                componentsObj[c.id] = componentLabel
            })
        }
        return componentsObj
    }, [components, displayEnLabel, componentsConfig])

    if (multiple) {
        return multipleLabels
    } else {
        return label ? label : "";
    }
}
